import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCircle, FaDatabase } from 'react-icons/fa';
import courseMasterService from '../services/courseMasterService.jsx';
// Import other services as you create them
// import userMasterService from '../services/userMasterService.jsx';
// import projectMasterService from '../services/projectMasterService.jsx';

const Masters = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const masters = [
    { name: 'ActiveProjectMaster', apiEndpoint: '/api/active-projects' },
    { name: 'ClientProjectMaster', apiEndpoint: '/api/client-projects' },
    { name: 'CommunityHubMaster', apiEndpoint: '/api/community-hubs' },
    { name: 'CourseMaster', apiEndpoint: '/api/v1/courses/all', service: courseMasterService },
    { name: 'HubPostMaster', apiEndpoint: '/api/hub-posts' },
    { name: 'MediaMaster', apiEndpoint: '/api/media' },
    { name: 'NotificationMaster', apiEndpoint: '/api/notifications' },
    { name: 'PostReportMaster', apiEndpoint: '/api/post-reports' },
    { name: 'ProjectMaster', apiEndpoint: '/api/projects' },
    { name: 'QuestionMaster', apiEndpoint: '/api/questions' },
    { name: 'QuizMaster', apiEndpoint: '/api/quizzes' },
    { name: 'SubscriptionMaster', apiEndpoint: '/api/subscriptions' },
    { name: 'YourProjectMaster', apiEndpoint: '/api/your-projects' }
  ];

  const colors = [
    'text-blue-500', 'text-green-500', 'text-purple-500', 'text-red-500',
    'text-yellow-500', 'text-indigo-500', 'text-pink-500', 'text-teal-500',
    'text-orange-500', 'text-cyan-500', 'text-lime-500', 'text-emerald-500',
    'text-violet-500', 'text-rose-500'
  ];

  // Fetch counts for all masters
  useEffect(() => {
    const fetchAllCounts = async () => {
      setLoading(true);
      const countData = {};

      try {
        // Fetch counts in parallel for better performance
        const fetchPromises = masters.map(async (master) => {
          try {
            let count = 0;
            
            // For CourseMaster, use the existing service
            if (master.name === 'CourseMaster' && master.service) {
              const data = await master.service.getAllCourses();
              count = data.courses?.length || 0;
            } 
            // For other masters, you can add similar service calls
            // else if (master.name === 'UserMaster' && userMasterService) {
            //   const data = await userMasterService.getAllUsers();
            //   count = data.users?.length || 0;
            // }
            else {
              // Placeholder: Replace with actual API call when available
              // For now, generate random count for demo
              count = Math.floor(Math.random() * 50) + 1;
            }
            
            return { name: master.name, count };
          } catch (error) {
            console.error(`Error fetching count for ${master.name}:`, error);
            return { name: master.name, count: 0 };
          }
        });

        const results = await Promise.all(fetchPromises);
        results.forEach(result => {
          countData[result.name] = result.count;
        });
        
        setCounts(countData);
      } catch (error) {
        console.error('Error fetching counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllCounts();
  }, []);

  const handleViewDetails = (masterName) => {
    navigate(`/masters/${masterName}`);
  };

  const getCountColor = (count) => {
    if (count === 0) return 'bg-gray-500';
    if (count < 10) return 'bg-yellow-500';
    if (count < 50) return 'bg-green-500';
    if (count < 100) return 'bg-blue-500';
    return 'bg-purple-500';
  };

  const getCountIcon = (count) => {
    if (count === 0) return '📭';
    if (count < 10) return '📝';
    if (count < 50) return '📚';
    return '🎯';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Masters Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage all master data files for the Community of Hubs platform
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading counts...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {masters.map((master, index) => {
            const count = counts[master.name] || 0;
            return (
              <div
                key={master.name}
                className="group bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-200 dark:border-gray-700 relative overflow-hidden"
                onClick={() => handleViewDetails(master.name)}
              >
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Count Badge */}
                <div className="absolute top-3 right-3 z-10">
                  <div className={`${getCountColor(count)} text-white rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1 shadow-md`}>
                    <FaDatabase className="text-xs" />
                    <span>{count.toLocaleString()} {count === 1 ? 'Entry' : 'Entries'}</span>
                  </div>
                </div>

                <div className="p-6 relative z-10">
                  <div className="flex items-center mb-4">
                    <FaCircle className={`text-sm mr-3 ${colors[index % colors.length]}`} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {master.name}
                    </h3>
                  </div>
                  
                  {/* Statistics bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span>Record Count</span>
                      <span>{count} / 500</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`${getCountColor(count)} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${Math.min((count / 500) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    Manage {master.name.replace('Master', '').replace(/([A-Z])/g, ' $1').trim().toLowerCase()} data
                  </p>
                  
                  <button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(master.name);
                    }}
                  >
                    <span>View Details</span>
                    <span className="bg-white bg-opacity-20 rounded-full px-2 py-0.5 text-xs">
                      {count}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Card */}
      {!loading && (
        <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Total Records</h3>
              <p className="text-gray-600 dark:text-gray-400">Across all masters</p>
            </div>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {Object.values(counts).reduce((sum, count) => sum + count, 0).toLocaleString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Masters;